import React, { useEffect, useState } from 'react';
import LoadingSkeleton from 'src/components/ui/LoadingSkeleton/LoadingSkeleton';
import { getToken } from 'src/services/authToken';

/**
 * The cache holds base64 payloads of product images and company logos that were
 * fetched with the signed-in user's bearer token — tenant data, not device
 * state. `localStorage` kept them for two days across sessions and across tabs,
 * so on a shared workstation the next user (or nobody at all, after the browser
 * was simply closed) still had them on disk.
 *
 * `sessionStorage` caps that: the cache dies with the tab even if no logout ever
 * runs. Logging out additionally purges it explicitly (`clearImageCache`),
 * because a logout/login in the *same* tab keeps sessionStorage alive.
 *
 * Cost of the move: one extra fetch per image per new tab. The two-day TTL still
 * applies within a tab.
 */
type CachedImage = {
  url: string; // Store as Base64 instead of blob URL
  expiry: number;
};

// Cache duration of 2 days
const CACHE_EXPIRY_MS = 2 * 24 * 60 * 60 * 1000;

const getStorage = (): Storage => sessionStorage;

// Add function to clear cache of a specific image
export const clearCachedImage = (imageUrl: string) => {
  const storage = getStorage();
  storage.removeItem(imageUrl);
};

/** True for a value written by `setCachedImage` — the keys are raw image URLs. */
const isCachedImageEntry = (raw: string | null): boolean => {
  if (!raw || raw[0] !== '{') return false;
  try {
    const parsed = JSON.parse(raw);
    return (
      typeof parsed === 'object' &&
      parsed !== null &&
      typeof parsed.url === 'string' &&
      parsed.url.startsWith('data:') &&
      typeof parsed.expiry === 'number'
    );
  } catch {
    return false;
  }
};

const purgeCachedImages = (storage: Storage) => {
  const keys: string[] = [];
  for (let i = 0; i < storage.length; i += 1) {
    const key = storage.key(i);
    if (key !== null && isCachedImageEntry(storage.getItem(key))) keys.push(key);
  }
  keys.forEach(key => storage.removeItem(key));
};

/**
 * Drops every cached image. Called from `resetClientState()` on logout.
 *
 * Sweeps `localStorage` as well, so the entries devices still carry from when
 * the cache lived there are cleaned up on the next logout instead of sitting
 * around until their TTL expires.
 */
export const clearImageCache = () => {
  try {
    purgeCachedImages(sessionStorage);
  } catch {
    /* ignore disabled storage */
  }
  try {
    purgeCachedImages(localStorage);
  } catch {
    /* ignore disabled storage */
  }
};

interface ImageProps {
  imageUrl: string | null;
  alt: string;
  className?: string;
  fallbackComponent: React.ReactElement;
  noCache?: boolean;
  onResolved?: (hasImage: boolean) => void;
}

const Image: React.FC<ImageProps> = ({ imageUrl, alt, className, fallbackComponent, noCache = false, onResolved }) => {
  const [imgSrc, setImgSrc] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const getCachedImage = (key: string): CachedImage | null => {
    const storage = getStorage();
    const cachedData = storage.getItem(key);
    if (!cachedData) return null;

    const parsedData: CachedImage = JSON.parse(cachedData);
    if (parsedData.expiry > Date.now()) {
      return parsedData;
    }
    storage.removeItem(key); // Remove expired image
    return null;
  };

  const setCachedImage = (key: string, data: CachedImage) => {
    const storage = getStorage();
    storage.setItem(key, JSON.stringify(data));
  };

  const blobToBase64 = (blob: Blob): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob); // Convert blob to Base64
    });
  };

  useEffect(() => {
    const accessToken = getToken();

    const fetchImage = async () => {
      if (!imageUrl) {
        setHasError(true);
        setIsLoading(false);
        return;
      }

      // Remove image in case it's already an image blob (in case of image upload preview)
      if (imageUrl.includes('blob:')) {
        setImgSrc(imageUrl);
        setIsLoading(false);
        setHasError(false);
        return;
      }

      const cachedImage = getCachedImage(imageUrl);
      if (!noCache && cachedImage && cachedImage.expiry > Date.now()) {
        setImgSrc(cachedImage.url); // Use cached Base64 image if valid
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setHasError(false);

      try {
        const response = await fetch(imageUrl, {
          // `Image` is also rendered on the public tracking/return pages, where
          // there is no token — send no header at all instead of the literal
          // string "Bearer null".
          headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined,
        });

        if (!response.ok) throw new Error('Failed to fetch image');

        const contentType = response.headers.get('content-type') || '';
        if (!contentType.startsWith('image/')) throw new Error('Not an image response');

        const blob = await response.blob();
        const base64Image = await blobToBase64(blob); // Convert blob to Base64

        // Cache the Base64 image with expiration time. Caching is best-effort:
        // a localStorage quota error on large images must not blank the preview.
        const newCachedImage = { url: base64Image, expiry: Date.now() + CACHE_EXPIRY_MS };
        if (!noCache) {
          try {
            setCachedImage(imageUrl, newCachedImage);
          } catch {
            // ignore storage errors (e.g. QuotaExceededError)
          }
        }

        setImgSrc(base64Image);
      } catch {
        setHasError(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchImage();
  }, [imageUrl]);

  useEffect(() => {
    if (!isLoading) onResolved?.(!hasError && !!imgSrc);
  }, [isLoading, hasError, imgSrc]);

  if (isLoading) {
    return (
      <div className={`${className} relative`}>
        <LoadingSkeleton />
      </div>
    );
  }

  if (hasError || !imgSrc) {
    return fallbackComponent;
  }

  return <img src={imgSrc} alt={alt} className={className} />;
};

export default Image;
